/**
 * /settings (v8, todos os papéis) — hub de Configurações com a estrutura
 * COMPLETA do TiFlux: título + busca à direita, acordeões por área (Geral,
 * Mesa de serviço, Usuários, Recurso, Financeiro) com os itens em DUAS COLUNAS
 * de links de texto. Item com destino navega; item ainda sem funcionalidade
 * fica desabilitado com badge "Em breve" (a busca também os encontra). Card de
 * resumo à direita (Usuários/Mesas/Clientes).
 */
import { useState, type ComponentType } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import {
  ChevronDown,
  CircleDollarSign,
  HardDrive,
  Hash,
  LayoutGrid,
  Search,
  UserRound,
  Users2,
} from 'lucide-react';
import { TEMPLATE_VARIABLE_NAMES, type PermissionKey } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { Badge, Dialog, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { desksListQuery, usersListQuery } from '@/features/tickets/tickets-queries';
import { clientsLookupOptions } from '@/features/desks/desks-queries';
import { normalizeText } from '@/features/settings/settings-ui';

type IconType = ComponentType<{ className?: string; strokeWidth?: number }>;

interface SettingsItem {
  key: string;
  label: string;
  /** linha auxiliar opcional (mantém a linha compacta quando ausente) */
  description?: string;
  /** destino de navegação (ausente quando o item abre um dialog ou é futuro) */
  to?: string;
  /** abre um dialog em vez de navegar */
  opens?: 'variables';
  /** funcionalidade ainda não disponível — linha desabilitada + "Em breve" */
  soon?: boolean;
  adminOnly?: boolean;
  /**
   * v7: item ESCONDIDO quando me.permissions veio sem esta key (can() do
   * usePermissions; o admin vê tudo, e o usuário sem grupo de permissão vê tudo menos
   * o que é ADMIN_DEFAULT_ONLY_KEYS — a gestão de acesso e de sistema). Só as
   * telas de GESTÃO são gated — navegação de leitura fica sempre visível.
   * v9: array = basta UMA das keys (ex.: contracts.view OU contracts.manage).
   */
  permission?: PermissionKey | PermissionKey[];
}

interface SettingsGroup {
  key: string;
  label: string;
  icon: IconType;
  items: SettingsItem[];
}

// Estrutura espelhada do TiFlux real (grupo → itens, em ordem alfabética como
// lá). Itens sem correspondente no app ficam listados como "Em breve".
const GROUPS: SettingsGroup[] = [
  {
    key: 'geral',
    label: 'Geral',
    icon: LayoutGrid,
    items: [
      {
        key: 'campos-personalizados',
        label: 'Campos personalizados',
        to: '/settings/entities',
        description: 'Grupos de campos por ticket, recurso e cliente.',
        permission: 'custom_fields.manage',
      },
      {
        key: 'chat',
        label: 'Chat',
        description: 'Widget de atendimento do seu site.',
        to: '/settings/chat',
        adminOnly: true,
      },
      // PoolTrack, 02/10 — junto do Chat: mesmas conversas. Leitura de quem
      // atende (a mesma lista do compositor); a página esconde a escrita.
      {
        key: 'respostas-rapidas',
        label: 'Respostas rápidas',
        description: 'Mensagens prontas da conversa, com /atalho.',
        to: '/settings/quick-replies',
        permission: ['chat.access', 'integrations.manage'],
      },
      {
        key: 'email',
        label: 'E-mail',
        description: 'Modelos de e-mail e log de envio.',
        to: '/settings/desks/models',
      },
      {
        key: 'formulario-externo',
        label: 'Formulário externo',
        description: 'Ative por mesa, na seção Formulário externo.',
        to: '/settings/desks',
      },
      {
        key: 'integracoes',
        label: 'Integrações',
        description: 'WhatsApp, financeiro e dados.',
        to: '/settings/integrations',
        adminOnly: true,
        permission: 'integrations.manage',
      },
      // v10 Etapa 2 — junto de Integrações (não há grupo Comunicação no hub):
      // mesma regra de acesso da API (integrations.manage).
      {
        key: 'campanhas',
        label: 'Campanhas',
        description: 'Envio em massa de WhatsApp por modelo.',
        to: '/settings/campaigns',
        permission: 'integrations.manage',
      },
      {
        key: 'importacoes',
        label: 'Importações',
        description: 'Clientes e recursos por planilha CSV.',
        to: '/settings/imports',
        adminOnly: true,
      },
      {
        key: 'ia',
        label: 'Inteligência artificial',
        description: 'Sugestão de resposta e resumo de tickets.',
        to: '/settings/ai',
        adminOnly: true,
      },
      {
        key: 'organizacao',
        label: 'Organização',
        description: 'Dados da conta, e-mail e logo.',
        to: '/settings/organization',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'mesa',
    label: 'Mesa de serviço',
    icon: Hash,
    items: [
      {
        key: 'mesas',
        label: 'Mesas de serviço',
        description: 'Fases, status, prioridades, SLA e expediente.',
        to: '/settings/desks',
      },
      {
        key: 'atividades-agendadas',
        label: 'Atividades agendadas',
        description: 'Tickets recorrentes de todas as mesas.',
        to: '/settings/desks/scheduled',
      },
      {
        key: 'automacoes',
        label: 'Automações',
        description: 'Regras por mesa ou para todas as mesas.',
        to: '/settings/desks/automations',
      },
      { key: 'catalogos', label: 'Catálogos de serviços', to: '/settings/desks/catalogs' },
      {
        key: 'checklists',
        label: 'Checklists',
        description: 'Modelos de checklist de todas as mesas.',
        to: '/settings/desks/checklists',
      },
      {
        key: 'feriados',
        label: 'Feriados',
        description: 'Datas que pausam o SLA — valem para todas as mesas.',
        to: '/settings/desks/holidays',
      },
      {
        key: 'grupo-de-tickets',
        label: 'Grupo de tickets',
        description: 'Agrupe tickets entre mesas.',
        to: '/settings/ticket-groups',
      },
      {
        key: 'tecnicos',
        label: 'Técnicos',
        description: 'Quem atende as ordens de serviço: cidades, capacidade e ausências.',
        to: '/settings/technicians',
      },
      {
        key: 'modelos',
        label: 'Modelos',
        description: 'Modelos de resposta, apontamento e e-mail.',
        to: '/settings/desks/models',
      },
      {
        key: 'variaveis',
        label: 'Variáveis',
        description: 'Variáveis disponíveis nos modelos.',
        opens: 'variables',
      },
    ],
  },
  {
    key: 'usuarios',
    label: 'Usuários',
    icon: UserRound,
    items: [
      {
        key: 'usuarios',
        label: 'Usuários',
        description: 'Contas, papéis e acesso dos usuários.',
        to: '/settings/users',
        adminOnly: true,
        permission: 'users.manage',
      },
      { key: 'grupos-atendentes', label: 'Grupos de atendentes', to: '/settings/groups' },
      // spec 015: visível a todos (a equipe vê quem está em pausa); motivos e
      // "encerrar a pausa de outro" só para o admin, dentro da página
      {
        key: 'pausas',
        label: 'Pausas',
        description: 'Quem está em pausa agora e os motivos de pausa.',
        to: '/settings/pauses',
      },
      {
        key: 'grupos-permissoes',
        label: 'Grupos de permissões',
        description: 'O que cada atendente pode ver e fazer.',
        to: '/settings/permission-groups',
        permission: 'permission_groups.manage',
      },
      // spec 020: auditoria do "ver como cliente" — mesma key de quem emite
      {
        key: 'sessoes-depuracao',
        label: 'Sessões de depuração',
        description: 'Quem viu o portal como um usuário do cliente, quando e por quê.',
        to: '/settings/impersonation',
        permission: 'users.impersonate',
      },
    ],
  },
  {
    key: 'recurso',
    label: 'Recurso',
    icon: HardDrive,
    items: [
      {
        key: 'tipos-de-recurso',
        label: 'Tipos de recurso',
        description: 'Gerencie em Recursos.',
        to: '/resources',
      },
      { key: 'agente-remoto', label: 'Agente remoto', soon: true },
      { key: 'gatilhos', label: 'Gatilhos', soon: true },
      { key: 'monitoramentos', label: 'Monitoramentos', soon: true },
      { key: 'recursos-de-backup', label: 'Recursos de backup', soon: true },
      { key: 'scripts', label: 'Scripts', soon: true },
    ],
  },
  {
    key: 'financeiro',
    label: 'Financeiro',
    icon: CircleDollarSign,
    items: [
      {
        key: 'tipos-de-contrato',
        label: 'Tipos de contrato',
        description: 'Aba Contratos.',
        to: '/contracts',
      },
      {
        key: 'deslocamentos',
        label: 'Deslocamentos',
        description: 'Valores de deslocamento para lançar nos tickets.',
        to: '/settings/displacements',
        permission: ['contracts.view', 'contracts.manage'],
      },
      {
        key: 'servicos-avulsos',
        label: 'Serviços avulsos',
        description: 'Valores-hora interno, remoto e externo.',
        to: '/settings/loose-services',
        permission: ['contracts.view', 'contracts.manage'],
      },
      {
        key: 'valores-extras',
        label: 'Valores extras',
        description: 'Valores unitários para lançar nos tickets.',
        to: '/settings/extra-values',
        permission: ['contracts.view', 'contracts.manage'],
      },
    ],
  },
];

export function SettingsPage() {
  const user = useAuthUser();
  const { can, ready: permissionsReady } = usePermissions();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [dialog, setDialog] = useState<'variables' | null>(null);

  const isAdmin = user?.role === 'admin';
  const q = normalizeText(query.trim());
  const searching = q.length > 0;

  // Itens admin-only somem para os demais papéis (Integrações, Usuários);
  // itens com `permission` somem quando me.permissions veio sem a key (v7) —
  // e ficam escondidos enquanto o /me não resolveu (aparecer depois é melhor
  // que aparecer-e-sumir).
  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (item) =>
        (!item.adminOnly || isAdmin) &&
        (!item.permission ||
          (permissionsReady &&
            (Array.isArray(item.permission) ? item.permission.some(can) : can(item.permission))))
    ),
  })).filter((g) => g.items.length > 0);

  // Com busca ativa: só grupos com itens que casam (nome do grupo casa ⇒ todos
  // os itens; itens "Em breve" também contam), sempre expandidos. Sem busca:
  // acordeão controlado pelo clique.
  const filteredGroups = searching
    ? visibleGroups
        .map((g) => ({
          ...g,
          items: normalizeText(g.label).includes(q)
            ? g.items
            : g.items.filter(
                (item) =>
                  normalizeText(item.label).includes(q) ||
                  normalizeText(item.description ?? '').includes(q)
              ),
        }))
        .filter((g) => g.items.length > 0)
    : visibleGroups;

  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const activate = (item: SettingsItem) => {
    if (item.soon) return;
    if (item.opens) {
      setDialog(item.opens);
      return;
    }
    if (item.to) void navigate({ to: item.to });
  };

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      {/* ── Header: título + busca ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl leading-7 font-semibold">Configurações</h1>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar"
            aria-label="Buscar configuração"
            className="h-9 w-56 rounded-md border border-border bg-card pl-3 pr-8 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary sm:w-64"
          />
          <Search className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <div className="mt-4 flex flex-col-reverse gap-6 lg:flex-row lg:items-start">
        {/* ── Acordeões ──────────────────────────────────────────────────── */}
        <div className="min-w-0 flex-1">
          {filteredGroups.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Nenhuma configuração encontrada para “{query.trim()}”.
            </p>
          ) : (
            <ul>
              {filteredGroups.map((group) => {
                const expanded = searching || Boolean(open[group.key]);
                return (
                  <li key={group.key} className="border-b border-divider">
                    <button
                      type="button"
                      onClick={() => toggle(group.key)}
                      aria-expanded={expanded}
                      className="flex h-14 w-full items-center gap-3 px-1 text-left transition-colors hover:bg-accent/40"
                    >
                      <group.icon className="size-[18px] shrink-0 text-primary" />
                      <span className="flex-1 truncate text-[15px] font-medium">{group.label}</span>
                      <ChevronDown
                        className={cn(
                          'size-4 shrink-0 text-muted-foreground transition-transform',
                          expanded && 'rotate-180'
                        )}
                      />
                    </button>
                    {expanded ? (
                      // Duas colunas de links de texto, como no TiFlux.
                      <ul className="grid grid-cols-1 gap-x-8 pb-3 pl-8 pr-2 sm:grid-cols-2">
                        {group.items.map((item) => (
                          <li key={item.key}>
                            {item.soon ? (
                              <div
                                aria-disabled
                                className="flex items-center gap-2 rounded-md px-2 py-1.5"
                              >
                                <span className="truncate text-sm text-muted-foreground">
                                  {item.label}
                                </span>
                                <Badge
                                  variant="outline"
                                  className="px-1.5 py-0 text-[11px] text-muted-foreground"
                                >
                                  Em breve
                                </Badge>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => activate(item)}
                                className="w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/60"
                              >
                                <span className="block truncate text-sm font-medium">
                                  {item.label}
                                </span>
                                {item.description ? (
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {item.description}
                                  </span>
                                ) : null}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── Card de resumo ─────────────────────────────────────────────── */}
        <SummaryCard />
      </div>

      {dialog === 'variables' ? <VariablesDialog onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

/** Rótulos de apoio para cada variável de template (fonte: @erp/shared). */
const VARIABLE_DESCRIPTIONS: Record<string, string> = {
  'ticket.number': 'Número do ticket.',
  'ticket.title': 'Título do ticket.',
  'ticket.stage': 'Fase atual do ticket.',
  'ticket.status': 'Status atual do ticket.',
  'ticket.priority': 'Prioridade do ticket (vazio se não definida).',
  'client.name': 'Nome do cliente do ticket.',
  'desk.name': 'Nome da mesa do ticket.',
  'responsible.name': 'Nome do responsável (vazio se não atribuído).',
};

/**
 * Dialog informativo com as variáveis `{{chave}}` suportadas nos modelos de
 * resposta/apontamento e nos templates de e-mail — lista TEMPLATE_VARIABLE_NAMES
 * de @erp/shared, a mesma fonte usada pela API ao renderizar.
 */
function VariablesDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} title="Variáveis de modelo">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Use estas variáveis nos modelos de resposta, apontamento e e-mail escrevendo{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">{'{{variável}}'}</code> no corpo do
          texto. Na hora do envio cada uma é substituída pelo valor do ticket; variável sem valor
          vira texto vazio.
        </p>
        <ul className="divide-y divide-divider rounded-md border border-divider">
          {TEMPLATE_VARIABLE_NAMES.map((name) => (
            <li key={name} className="flex items-baseline gap-3 px-3 py-2">
              <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {`{{${name}}}`}
              </code>
              <span className="min-w-0 text-sm text-muted-foreground">
                {VARIABLE_DESCRIPTIONS[name] ?? ''}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

/**
 * Contadores da conta (Usuários/Mesas/Clientes) — como o card do TiFlux.
 * "Usuários" é o TOTAL do servidor (as pessoas deste tenant, equipe e portal):
 * uma página de 1 só para ler o `X-Total-Items` — o tamanho da lista cortada
 * em 200 dizia "200" para a PoolTrack, que tem mais de 270 (F-108).
 */
function SummaryCard() {
  const [usersQ, desksQ, clientsQ] = useQueries({
    queries: [
      usersListQuery({ q: '', page: 1, pageSize: 1 }),
      desksListQuery(),
      clientsLookupOptions(),
    ],
  });

  return (
    <aside className="w-full shrink-0 rounded-lg border bg-card p-4 shadow-xs lg:w-80">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5">
        <SummaryStat
          label="Usuários"
          icon={UserRound}
          className="text-[#1677FF]"
          count={usersQ.data?.total}
          pending={usersQ.isPending}
        />
        <SummaryStat
          label="Mesas configuradas"
          icon={Hash}
          className="text-primary"
          count={desksQ.data?.length}
          pending={desksQ.isPending}
        />
        <SummaryStat
          label="Clientes"
          icon={Users2}
          className="text-[#D48806]"
          count={clientsQ.data?.length}
          pending={clientsQ.isPending}
        />
      </div>
    </aside>
  );
}

function SummaryStat({
  label,
  icon: Icon,
  className,
  count,
  pending,
}: {
  label: string;
  icon: IconType;
  className: string;
  count: number | undefined;
  pending: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      {pending ? (
        <Skeleton className="mt-1.5 h-6 w-14" aria-busy />
      ) : (
        <div className={cn('mt-1 flex items-center gap-1.5', className)}>
          <Icon className="size-4 shrink-0" />
          <span className="text-lg leading-6 font-semibold tabular-nums">{count ?? '—'}</span>
        </div>
      )}
    </div>
  );
}
